/**
 * Hook to fetch and track pending intakes count
 */

import { useState, useEffect, useCallback } from 'react';
import { getIntakeMetrics, getUnreviewedCount } from '@/services/intakeService';

// Custom event name for triggering intake count refresh
export const INTAKE_REVIEWED_EVENT = 'intake-reviewed';

// Helper function to dispatch the refresh event (call this after reviewing an intake)
export function notifyIntakeReviewed() {
  window.dispatchEvent(new CustomEvent(INTAKE_REVIEWED_EVENT));
}

interface PendingIntakesState {
  pendingCount: number;
  newResponsesCount: number;
  totalCount: number;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

export function usePendingIntakes(clinicId?: string): PendingIntakesState {
  const [pendingCount, setPendingCount] = useState(0);
  const [newResponsesCount, setNewResponsesCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCounts = useCallback(async () => {
    if (!clinicId) return;
    try {
      const [metrics, unreviewedResult] = await Promise.all([
        getIntakeMetrics(
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          new Date().toISOString(),
          clinicId
        ),
        getUnreviewedCount(clinicId)
      ]);

      if (metrics.success && metrics.data) {
        setPendingCount(metrics.data.pending || 0);
      }

      if (unreviewedResult.success && unreviewedResult.data) {
        setNewResponsesCount(unreviewedResult.data.count);
      }
    } catch (error) {
      console.error('Error fetching pending intakes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    fetchCounts();
    // Refresh every 20 minutes
    const interval = setInterval(fetchCounts, 1200000);
    
    // Listen for intake reviewed events to refresh immediately
    const handleIntakeReviewed = () => {
      fetchCounts();
    };
    window.addEventListener(INTAKE_REVIEWED_EVENT, handleIntakeReviewed);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener(INTAKE_REVIEWED_EVENT, handleIntakeReviewed);
    };
  }, [fetchCounts]);

  return {
    pendingCount,
    newResponsesCount,
    totalCount: pendingCount + newResponsesCount,
    isLoading,
    refresh: fetchCounts
  };
}
